import Image from 'next/image';
import { useTranslations } from 'next-intl';
import { ReactNode } from 'react';

import OtzovikIcon from '@/fo/icons/base/logos/otzovik.svg';
import OzonIcon from '@/fo/icons/base/logos/ozon.svg';
import WildberriesIcon from '@/fo/icons/base/logos/wildberries.svg';
import { UiKitCarouselBase } from '@/fo/ui/kit/carousel/base';
import { UiKitLabelBase } from '@/fo/ui/kit/label/base';
import { UiKitRatingBase } from '@/fo/ui/kit/rating/base';
import { ReviewImage } from '@/fo/ui/review/gallery/base';
import { utilPluralize } from '@/fo/util/pluralize';

import iRecommendImage from '../../assets/irecommend.webp';
import YaMarketImage from '../../assets/yandex-market-logo.png';
import { Review, ReviewService } from '../../types';
import { ReviewContentBlock } from '../ReviewContentBlock/ReviewContentBlock';
import { ReviewResponseCard } from '../ReviewResponseCard/ReviewResponseCard';

import { styles } from './ReviewCard.styles';

export interface ReviewCardProps {
  review: Review;
  onImageClick?: (imageId: string, images: ReviewImage[]) => void;
}

export function ReviewCard({ review, onImageClick }: ReviewCardProps) {
  const t = useTranslations();
  const slots = styles();

  const pluralizeAge = utilPluralize({
    one: t('pluralize.age.one'),
    few: t('pluralize.age.few'),
    many: t('pluralize.age.many')
  });

  const ageText = review.author.age
    ? `${review.author.age} ${pluralizeAge(review.author.age)}`
    : null;

  const serviceIconsMap: Record<ReviewService, ReactNode> = {
    otzovik: <OtzovikIcon className={slots.service()} />,
    irecommend: (
      <Image
        src={iRecommendImage}
        alt="IRecommend"
        width={188}
        height={32}
        className={slots.service()}
      />
    ),
    ozon: <OzonIcon className={slots.service()} />,
    wildberries: <WildberriesIcon className={slots.service()} />,
    yandexMarket: (
      <Image
        src={YaMarketImage}
        alt="YaMarket"
        width={188}
        height={32}
        className={slots.service()}
      />
    )
  };

  return (
    <div className={slots.base()}>
      <div className={slots.header()}>
        <UiKitRatingBase value={review.rating} />
        <span className={slots.date()}>{review.date}</span>
      </div>
      <div className={slots.subHeader()}>
        <div className={slots.author()}>
          <span>{review.author.name}</span>
          {review.author.influencer && (
            <UiKitLabelBase
              variant="default"
              color="yellow"
            >
              {t('review.labels.influencer')}
            </UiKitLabelBase>
          )}
        </div>
        <div className={slots.authorInfo()}>
          {ageText}
          {review.author.skinType && ageText && ' / '}
          {review.author.skinType}
          {review.author.skinType && ageText && ` ${t('skin')}`}
        </div>
      </div>

      {review.images.length > 0 && (
        <div className={slots.gallery()}>
          <UiKitCarouselBase
            showArrows={false}
            loop={false}
            className={slots.carousel()}
            dragFree={true}
          >
            {review.images.map((img) => (
              <button
                key={img.id}
                type="button"
                className={slots.imageButton()}
                onClick={() => onImageClick?.(img.id, review.images)}
              >
                <Image
                  src={img.url}
                  alt={img.alt}
                  width={100}
                  height={100}
                  className={slots.image()}
                />
              </button>
            ))}
          </UiKitCarouselBase>
        </div>
      )}

      <div className={slots.content()}>
        <ReviewContentBlock
          title={t('review.labels.pros')}
          content={review.pros}
        />

        <ReviewContentBlock
          title={t('review.labels.cons')}
          content={review.cons}
        />

        <ReviewContentBlock
          title={t('review.labels.comment')}
          content={review.comment}
        />
      </div>

      {review.response && (
        <ReviewResponseCard
          text={review.response.text}
          recommendedProduct={review.response.recommendedProduct}
        />
      )}

      {(review.showMoreLink || review.source) && (
        <div className={slots.sourceBlock()}>
          {review.showMoreLink && (
            <a
              href={review.showMoreLink}
              target="_blank"
              rel="noopener noreferrer"
              className={slots.showMoreLink()}
            >
              {t('review.buttons.readMore')}
            </a>
          )}

          {review.source && !review.showMoreLink && (
            <a
              href={review.source.url}
              target="_blank"
              rel="noopener noreferrer"
              className={slots.sourceLink()}
            >
              {serviceIconsMap[review.source.type]}
            </a>
          )}

          {review.source && review.showMoreLink && (
            <div className={slots.sourceLink()}>
              {serviceIconsMap[review.source.type]}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
